import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { DTOFileInfo } from 'src/apis/dto/DTOFileInfo';
import { remoteConfigAtom } from 'src/stores/auth/atoms';
import {
  handleDisconnectAtom,
  matchConfigAtom,
  matchInfoAtom,
  stompSendAtom,
  validateImageAndReportMatchAtom,
} from 'src/stores/match/atoms';
import { hvmFeaturesCapturedAtom } from 'src/stores/ml/atoms';
import { DETECTION_SOURCE, MLFeatureType } from 'src/stores/ml/types';
import { showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ReportMatchMethod } from 'src/types/Match';
import { ToastType } from 'src/types/Toast';
import { isObjectKeyTrue } from 'src/utils/common';
import { pushEvent } from 'src/utils/event-gateway';

// Android MatchSession.underageTriggerStateFlow 참조
// Warn: ChildDetected 메시지 송신 (경고), Report: 자동신고 + 매치 종료

type UnderageTriggerState = { kind: 'Idle' } | { kind: 'Warn'; count: number } | { kind: 'Report' };

interface UnderageTriggerContext {
  state: UnderageTriggerState;
  /** throttleFirst용 — 마지막 emit 시각(ms) */
  lastEmitTime: number;
}

interface VictimToastContext {
  requestId?: string;
  detectionSource: DETECTION_SOURCE;
  matchId: string;
}

const UNDERAGE_WARNING_MAX_COUNT_DEFAULT = 2;
const UNDERAGE_DETECTION_INTERVAL_SEC_DEFAULT = 5;

export const underageTriggerContextAtom = atomWithReset<UnderageTriggerContext>({
  state: { kind: 'Idle' },
  lastEmitTime: 0,
});

/**
 * HVS ScreeningEvent DETECTED+VICTIM 수신 시 서버가 제공한 fileInfo를 매치 단위로 보관.
 * after match popup에서 신고 시 이 fileInfo를 사용한다.
 */
interface HvsScreeningCapture {
  matchId: string;
  fileInfo?: DTOFileInfo;
}
export const hvsScreeningCaptureAtom = atom<HvsScreeningCapture | null>(null);

const showUnderageVictimToastAtom = atom(
  null,
  (get, set, { requestId, detectionSource, matchId }: VictimToastContext) => {
    const remoteConfig = get(remoteConfigAtom);
    if (!remoteConfig || !isObjectKeyTrue(remoteConfig, 'inMatchPeerUnderageDetectToastEnabled')) {
      return;
    }

    set(showToastAtom, {
      type: ToastType.WARNING,
      title: 'inmatch_underage_warning_title',
      message: 'inmatch_underage_warning_victim_desc',
    });
    pushEvent({
      eventType: EVENT_TYPE.REPORT,
      eventName: EVENT_NAME.MIRROR__IMPRESSION_CHILD_REPORTED_TOAST,
      eventParams: {
        action_category: 'impression',
        tab: 'mirror',
        page: 'match',
        target: 'child_reported_toast',
        request_id: requestId ?? '',
        detection_source: detectionSource,
        match_id: matchId,
      },
    });
  }
);

const disconnectWithUnderageVictimToastAtom = atom(
  null,
  async (_get, set, context: VictimToastContext) => {
    await set(handleDisconnectAtom, { reconnect: true, finisher: true });
    set(showUnderageVictimToastAtom, context);
  }
);

const sendStompChildDetectedAtom = atom(null, (get, set) => {
  const matchId = get(matchInfoAtom)?.matchId;
  if (!matchId) return;
  set(stompSendAtom, JSON.stringify({ type: 'ChildDetected', matchId }));
});

/**
 * MatchHVUnderageExistAutoReport 트리거 시 호출.
 * Android MatchSession.underageTriggerStateFlow의 filter → throttle → withIndex → map 체인에 대응.
 */
export const handleUnderageTriggerAtom = atom(null, async (get, set) => {
  const ctx = get(underageTriggerContextAtom);

  // 이미 Report 상태면 무시
  if (ctx.state.kind === 'Report') return;

  const matchInfo = get(matchInfoAtom);
  // peer whitelist 체크
  if (matchInfo?.peerProfile?.isUnderAgeAllowed) return;

  // throttleFirst
  const remoteConfig = get(remoteConfigAtom);
  const intervalSec =
    remoteConfig?.inMatchUnderageDetectionIntervalSec ?? UNDERAGE_DETECTION_INTERVAL_SEC_DEFAULT;
  const now = Date.now();
  if (now - ctx.lastEmitTime <= intervalSec * 1000) return;

  // 경고 최대 횟수
  const maxCount =
    remoteConfig?.inMatchUnderageWarningPopupDisplayCount ?? UNDERAGE_WARNING_MAX_COUNT_DEFAULT;
  const currentCount = ctx.state.kind === 'Warn' ? ctx.state.count : 0;

  if (currentCount >= maxCount) {
    set(underageTriggerContextAtom, { state: { kind: 'Report' }, lastEmitTime: now });
    if (!matchInfo?.matchId) return;

    // 자동신고 → 매치 종료 → 토스트 (이미지 캡쳐 실패해도 신고 + 매치 종료는 진행)
    const captureData = get(hvmFeaturesCapturedAtom)[MLFeatureType.MatchHVUnderageExistAutoReport];
    try {
      await set(validateImageAndReportMatchAtom, {
        file: captureData?.file ?? null,
        matchId: matchInfo.matchId,
        reportType: 'CHILD_AGE_ABUSE',
        reportMethod: ReportMatchMethod.IN_MATCH_AUTO_UNEXPOSED,
      });
    } catch (error) {
      Sentry.captureMessage('Failed to auto-report underage', {
        extra: { error, matchId: matchInfo.matchId },
      });
    } finally {
      // 리포트 프로미스가 이미 종료된 매치 이후에 settle될 수 있음 — 현재 세션이 원래 세션인지 확인
      if (get(matchInfoAtom)?.matchId === matchInfo.matchId) {
        try {
          await set(disconnectWithUnderageVictimToastAtom, {
            requestId: get(matchConfigAtom)?.requestId,
            detectionSource: DETECTION_SOURCE.ON_DEVICE,
            matchId: matchInfo.matchId,
          });
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    }
    return;
  }

  // Warn 상태 전이 + ChildDetected 송신
  const nextCount = currentCount + 1;
  set(underageTriggerContextAtom, { state: { kind: 'Warn', count: nextCount }, lastEmitTime: now });
  set(sendStompChildDetectedAtom);
});

/**
 * HVS ScreeningEvent phase=REPORTED, role=VICTIM 수신 시 호출.
 * 서버가 상대방을 미성년자로 신고 완료했으므로 매치를 종료하고, RC에 따라 토스트를 노출한다.
 * handleUnderageTriggerAtom의 Report 분기와 동일한 UX지만 클라이언트 측 이미지 업로드/신고는 수행하지 않는다.
 */
export const handleScreeningReportedAtom = atom(
  null,
  async (get, set, brokerMessage: { matchId: string }) => {
    if (brokerMessage.matchId !== get(matchInfoAtom)?.matchId) return;

    set(underageTriggerContextAtom, { state: { kind: 'Report' }, lastEmitTime: Date.now() });

    try {
      await set(disconnectWithUnderageVictimToastAtom, {
        requestId: get(matchConfigAtom)?.requestId,
        detectionSource: DETECTION_SOURCE.SERVER,
        matchId: brokerMessage.matchId,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }
);

/**
 * 상대방 미성년자 감지 브로커 메시지 수신 시 호출.
 * ChildDetected(HVM on-device) 및 ScreeningEvent(HVS server) 공통 핸들러.
 * Android ChatFragment.ChildDetected 핸들러 참조.
 */
export const handleChildDetectedReceivedAtom = atom(
  null,
  (
    get,
    set,
    context: {
      brokerMessage: { matchId: string; type?: 'ChildDetected' | 'ScreeningEvent' };
      requestId?: string;
    }
  ) => {
    const { brokerMessage, requestId } = context;
    const remoteConfig = get(remoteConfigAtom);
    if (!remoteConfig || !isObjectKeyTrue(remoteConfig, 'inMatchUnderageWarningFeatureEnabled'))
      return;
    if (brokerMessage.matchId !== get(matchInfoAtom)?.matchId) return;

    const detectionSource =
      brokerMessage.type === 'ScreeningEvent'
        ? DETECTION_SOURCE.SERVER
        : DETECTION_SOURCE.ON_DEVICE;

    set(showToastAtom, {
      type: ToastType.WARNING,
      title: 'inmatch_underage_warning_title',
      message: 'inmatch_underage_warning_desc',
      buttonText: 'inmatch_underage_warning_btn',
      duration: 5000,
      ignoreLineClamp: true,
      onClickButton: () => {
        pushEvent({
          eventType: EVENT_TYPE.REPORT,
          eventName: EVENT_NAME.MIRROR__CLICK_CHILD_ALERT_BUTTON,
          eventParams: {
            action_category: 'click',
            tab: 'mirror',
            page: 'match',
            target: 'child_alert_button',
            request_id: requestId ?? '',
            detection_source: detectionSource,
            match_id: brokerMessage.matchId,
          },
        });
      },
    });
    pushEvent({
      eventType: EVENT_TYPE.REPORT,
      eventName: EVENT_NAME.MIRROR__IMPRESSION_CHILD_ALERT_TOAST,
      eventParams: {
        action_category: 'impression',
        tab: 'mirror',
        page: 'match',
        target: 'child_alert_toast',
        request_id: requestId ?? '',
        detection_source: detectionSource,
        match_id: brokerMessage.matchId,
      },
    });
  }
);
